<template>
    <div class="shifts-tab">
        <skeleton-loader v-if="loading" type="shifts" />

        <template v-else>
            <div class="shifts-toolbar">
                <span class="shifts-toolbar-title">{{ $t('business.activities') }}</span>

                <div class="activity-chips">
                    <div v-for="activity in activities" :key="activity.id" class="activity-chip"
                        :class="{ 'activity-chip--on': filter.includes(activity.id) }"
                        :style="filter.includes(activity.id) ? { borderColor: activity.color } : null">
                        <button type="button" class="activity-chip-main" @click="toggleFilter(activity.id)">
                            <span class="activity-dot" :style="{ background: activity.color }" />
                            {{ activity.name }}
                        </button>
                        <button-tooltip top :tooltip="$t('business.edit_activity')">
                            <template v-slot:activator="{ on, attrs }">
                                <button type="button" class="activity-chip-edit" v-on="on" v-bind="attrs"
                                    @click="editActivity(activity)">
                                    <v-icon size="11">mdi-pencil</v-icon>
                                </button>
                            </template>
                        </button-tooltip>
                    </div>
                    <span v-if="activities.length === 0" class="shifts-empty-text">
                        {{ $t('business.no_activities') }}
                    </span>
                </div>

                <button-tooltip top :tooltip="$t('business.new_activity')">
                    <template v-slot:activator="{ on, attrs }">
                        <button type="button" class="icon-button" v-on="on" v-bind="attrs" @click="newActivity">
                            <v-icon size="15">mdi-plus</v-icon>
                        </button>
                    </template>
                </button-tooltip>

                <v-btn depressed small color="primary" class="black--text ml-auto" :disabled="activities.length === 0"
                    @click="newShift">
                    {{ $t('business.new_shift') }}
                </v-btn>
            </div>

            <div class="shifts-hint-bar">
                <div class="week-nav">
                    <button-tooltip top :tooltip="$t('business.previous_week')">
                        <template v-slot:activator="{ on, attrs }">
                            <button type="button" class="icon-button" v-on="on" v-bind="attrs" @click="changeWeek(-1)">
                                <v-icon size="15">mdi-chevron-left</v-icon>
                            </button>
                        </template>
                    </button-tooltip>

                    <button type="button" class="week-label" :class="{ 'week-label--past': !isCurrentWeek }"
                        @click="goToCurrentWeek">
                        {{ weekLabel }}
                    </button>

                    <button-tooltip top :tooltip="$t('business.next_week')">
                        <template v-slot:activator="{ on, attrs }">
                            <button type="button" class="icon-button" v-on="on" v-bind="attrs" @click="changeWeek(1)">
                                <v-icon size="15">mdi-chevron-right</v-icon>
                            </button>
                        </template>
                    </button-tooltip>
                </div>

                <span class="shifts-hint">{{ $t('business.shifts_grid_hint') }}</span>

                <div class="shifts-actions">
                    <export-button :filename="$t('business.schedule')" :build="buildExport" />

                    <v-btn depressed small outlined color="secondary" @click="$refs.statsDialog.show()">
                        {{ $t('business.see_stats') }}
                    </v-btn>
                </div>
            </div>

            <shifts-grid :shifts="visibleShifts" :activities="activities" :bookings="bookings" :week-start="weekStart"
                @click:shift="showMembers" @create="createShiftAt" @edit="editShift" @copy="askCopy" />
        </template>

        <activity-dialog ref="activityDialog" :used-count="usedCount" @save="onSaveActivity"
            @delete="onDeleteActivity" />
        <shift-dialog ref="shiftDialog" :activities="activities" :last-shift="lastShift" :bookings="bookings"
            @save="onSaveShift" @delete="onDeleteShift" />
        <shift-members-dialog ref="membersDialog" :shifts="shifts" :activities="activities" :clients="clients"
            :plan-types="planTypes" :bookings="bookings" :removing="removingMember" @add="onAddMember"
            @remove="onRemoveMember" @edit="editShift" />
        <shifts-stats-dialog ref="statsDialog" :shifts="shifts" :activities="activities" :bookings="bookings"
            :company-id="companyId" :week-start="weekStart" />

        <kimsa-confirm ref="copyConfirm" show-cancel :title="$t('business.copy_shift')" :content="copyText"
            :confirm-label="$t('business.copy')" :persistent="false" @confirm="confirmCopy" @cancel="copying = null" />
    </div>
</template>

<script>
import SkeletonLoader from "@/components/common/SkeletonLoader";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import ShiftsGrid from "@/components/company/business/ShiftsGrid";
import ActivityDialog from "@/components/company/business/ActivityDialog";
import ShiftDialog from "@/components/company/business/ShiftDialog";
import ShiftMembersDialog from "@/components/company/business/ShiftMembersDialog";
import ShiftsStatsDialog from "@/components/company/business/ShiftsStatsDialog";
import ExportButton from "@/components/company/business/ExportButton";
import { buildShiftsSheet } from "@/utils/BusinessExport";
import {
    addWeeks, bookShift, cancelBooking, deleteShift, invalidateRoster, isShiftFull, loadRoster, loadShiftBookings,
    loadShifts, mergeRoster, newId, saveSettings, saveShift, weekStartOf,
} from "@/utils/Business";

export default {
    name: "ShiftsTab",
    components: {
        SkeletonLoader, ButtonTooltip, KimsaConfirm, ShiftsGrid, ActivityDialog, ShiftDialog,
        ShiftMembersDialog, ShiftsStatsDialog, ExportButton,
    },
    props: {
        companyId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            loading: true,
            activities: [],
            planTypes: [],
            shifts: [],
            clients: [],
            bookings: {},
            weekStart: weekStartOf(),
            filter: [],
            editingActivity: null,
            removingMember: null,
            copying: null,
            lastShift: null,
        }
    },
    computed: {
        usedCount(vm = this) {
            if (!vm.editingActivity) return 0
            return vm.shifts.filter(shift => shift.activityId === vm.editingActivity.id).length
        },
        visibleShifts(vm = this) {
            if (vm.filter.length === 0) return vm.shifts
            return vm.shifts.filter(shift => vm.filter.includes(shift.activityId))
        },
        isCurrentWeek(vm = this) {
            return vm.weekStart === weekStartOf()
        },
        weekLabel(vm = this) {
            void vm.$i18n.locale
            const monday = vm.$moment(vm.weekStart, 'YYYY-MM-DD')
            return `${monday.format('DD MMM')} - ${monday.clone().add(6, 'days').format('DD MMM')}`
        },
        copyText(vm = this) {
            void vm.$i18n.locale
            if (!vm.copying) return ''
            const activity = vm.activities.find(item => item.id === vm.copying.shift.activityId)
            return vm.$t('business.copy_shift_confirm', {
                shift: activity?.name || vm.$t('business.no_activity'),
                day: vm.$moment().isoWeekday(vm.copying.day).format('dddd'),
                time: vm.copying.startTime,
            })
        },
    },
    mounted() {
        this.load()
    },
    methods: {
        async load() {
            const vm = this
            if (!vm.companyId) return
            vm.loading = true
            try {
                const [roster, shifts, bookings] = await Promise.all([
                    loadRoster(vm.companyId),
                    loadShifts(vm.companyId),
                    loadShiftBookings(vm.companyId, vm.weekStart),
                ])
                vm.activities = roster.activities
                vm.planTypes = roster.planTypes
                vm.clients = mergeRoster(roster.clients, roster.students)
                vm.shifts = shifts
                vm.bookings = bookings
            } catch (error) {
                console.error('[business] loadShifts', error?.code, error?.message)
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.loading = false
            }
        },
        async loadBookings() {
            const vm = this
            try {
                vm.bookings = await loadShiftBookings(vm.companyId, vm.weekStart)
            } catch (error) {
                console.error('[business] loadShiftBookings', error?.code, error?.message)
                vm.toast(vm.$t('common.error_generic'), 'error')
            }
        },
        changeWeek(offset) {
            const vm = this
            vm.weekStart = addWeeks(vm.weekStart, offset)
            vm.loadBookings()
        },
        goToCurrentWeek() {
            const vm = this
            if (vm.isCurrentWeek) return
            vm.weekStart = weekStartOf()
            vm.loadBookings()
        },
        toggleFilter(activityId) {
            const vm = this
            if (vm.filter.includes(activityId)) vm.filter = vm.filter.filter(id => id !== activityId)
            else vm.filter = [...vm.filter, activityId]
        },
        askCopy(payload) {
            const vm = this
            vm.copying = payload
            vm.$refs.copyConfirm?.show()
        },
        async confirmCopy() {
            const vm = this
            const payload = vm.copying
            vm.copying = null
            if (!payload) return

            const [startHour, startMinute] = payload.shift.startTime.split(':').map(Number)
            const [endHour, endMinute] = payload.shift.endTime.split(':').map(Number)
            const duration = ((endHour * 60 + endMinute) - (startHour * 60 + startMinute) + 1440) % 1440
            const [hour, minute] = payload.startTime.split(':').map(Number)
            const end = (hour * 60 + minute + duration) % 1440

            await vm.onSaveShift({
                activityId: payload.shift.activityId,
                day: payload.day,
                startTime: payload.startTime,
                endTime: `${String(Math.floor(end / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`,
                capacity: payload.shift.capacity,
            }, false)
        },
        buildExport() {
            const vm = this
            return buildShiftsSheet({
                shifts: vm.shifts,
                activities: vm.activities,
                bookings: vm.bookings,
                weekStart: vm.weekStart,
            })
        },
        newActivity() {
            const vm = this
            vm.editingActivity = null
            vm.$refs.activityDialog.show()
        },
        editActivity(activity) {
            const vm = this
            vm.editingActivity = activity
            vm.$refs.activityDialog.show(activity)
        },
        async onSaveActivity({ id, name, color }) {
            const vm = this
            const activities = [...vm.activities]
            const index = activities.findIndex(activity => activity.id === id)

            if (index >= 0) activities.splice(index, 1, { id, name, color })
            else activities.push({ id: newId(), name, color })

            await vm.persistActivities(activities)
        },
        async onDeleteActivity(activity) {
            const vm = this
            await vm.persistActivities(vm.activities.filter(item => item.id !== activity.id))
        },
        async persistActivities(activities) {
            const vm = this
            const previous = vm.activities
            vm.activities = activities
            invalidateRoster(vm.companyId)
            try {
                await saveSettings(vm.companyId, { activities })
            } catch (error) {
                vm.activities = previous
                vm.toast(vm.$t('common.error_generic'), 'error')
            }
        },
        newShift() {
            this.$refs.shiftDialog.show()
        },
        createShiftAt(preset) {
            const vm = this
            if (vm.activities.length === 0) return vm.toast(vm.$t('business.need_activity'), 'error')
            vm.$refs.shiftDialog.show(null, preset)
        },
        showMembers(shift) {
            this.$refs.membersDialog.show(shift)
        },
        editShift(shift) {
            const vm = this
            vm.$refs.membersDialog.hide()
            vm.$refs.shiftDialog.show(shift)
        },
        storeShift(shift) {
            const vm = this
            const index = vm.shifts.findIndex(item => item.id === shift.id)
            if (index >= 0) vm.shifts.splice(index, 1, shift)
            else vm.shifts = [...vm.shifts, shift]
        },
        async onSaveShift(shift, remember = true) {
            const vm = this
            const previous = vm.shifts.find(item => item.id === shift.id)
            try {
                const id = await saveShift(vm.companyId, shift)
                vm.storeShift({ ...shift, id, members: previous?.members || {} })
                if (!shift.id && remember) vm.lastShift = { ...shift, id }
            } catch (error) {
                vm.toast(vm.$t('common.error_generic'), 'error')
            }
        },
        storeBooking(shiftId, members) {
            this.bookings = { ...this.bookings, [shiftId]: members }
        },
        async onAddMember({ shift, key, member, fixed }) {
            const vm = this
            if (!shift || isShiftFull(shift, vm.bookings)) return

            const previousMembers = shift.members
            const previousBooking = vm.bookings[shift.id] || {}
            if (fixed) vm.storeShift({ ...shift, members: { ...previousMembers, [key]: { name: member.name } } })
            else vm.storeBooking(shift.id, { ...previousBooking, [key]: { name: member.name } })

            try {
                await bookShift(shift.id, vm.weekStart, key, member.name, fixed)
            } catch (error) {
                console.error('[business] bookShift', error?.code, error?.message)
                if (fixed) vm.storeShift({ ...shift, members: previousMembers })
                else vm.storeBooking(shift.id, previousBooking)
                vm.toast(vm.$t('common.error_generic'), 'error')
            }
        },
        async onRemoveMember({ shift, key, fixed }) {
            const vm = this
            if (!shift) return

            const previousMembers = shift.members
            const previousBooking = vm.bookings[shift.id] || {}
            vm.removingMember = key

            if (fixed) {
                const members = { ...previousMembers }
                delete members[key]
                vm.storeShift({ ...shift, members })
            } else {
                const members = { ...previousBooking }
                delete members[key]
                vm.storeBooking(shift.id, members)
            }

            try {
                await cancelBooking(shift.id, vm.weekStart, key, fixed)
            } catch (error) {
                console.error('[business] cancelBooking', error?.code, error?.message)
                if (fixed) vm.storeShift({ ...shift, members: previousMembers })
                else vm.storeBooking(shift.id, previousBooking)
                vm.toast(vm.$t('common.error_generic'), 'error')
            } finally {
                vm.removingMember = null
            }
        },
        async onDeleteShift(shift) {
            const vm = this
            const previous = vm.shifts
            vm.shifts = vm.shifts.filter(item => item.id !== shift.id)
            try {
                await deleteShift(shift.id, vm.companyId)
            } catch (error) {
                vm.shifts = previous
                vm.toast(vm.$t('common.error_generic'), 'error')
            }
        },
    },
}
</script>

<style scoped>
.shifts-tab {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.shifts-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.shifts-toolbar-title {
    font-size: var(--fs-label);
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.activity-chips {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    min-width: 0;
}

.activity-chip {
    display: flex;
    align-items: center;
    height: 26px;
    padding: 0 6px 0 8px;
    border-radius: 20px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    white-space: nowrap;
    transition: border-color 0.15s;
}

.activity-chip:hover {
    border-color: var(--v-outline2-base);
}

.activity-chip--on {
    background: var(--v-surface2-base);
}

.activity-chip-main {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 100%;
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    transition: color 0.15s;
}

.activity-chip--on .activity-chip-main {
    color: var(--v-txtPrimary-base);
}

.activity-chip-edit {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    margin-left: 4px;
    flex-shrink: 0;
    border-radius: 50%;
    color: var(--v-txtMuted-base);
    transition: color 0.15s, background 0.15s;
}

.activity-chip-edit:hover {
    background: var(--v-surface2-base);
    color: var(--v-txtPrimary-base);
}

.week-nav {
    display: flex;
    align-items: center;
    gap: 4px;
}

.week-label {
    min-width: 130px;
    height: 28px;
    padding: 0 10px;
    border-radius: 7px;
    font-size: var(--fs-caption);
    font-weight: 700;
    text-transform: capitalize;
    color: var(--v-txtPrimary-base);
    transition: background 0.15s;
}

.week-label:hover {
    background: var(--v-surface2-base);
}

.week-label--past {
    color: var(--v-txtSecondary-base);
}

.activity-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.icon-button {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border-radius: 7px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtSecondary-base);
    transition: color 0.15s, border-color 0.15s;
}

.icon-button:hover {
    color: var(--v-txtPrimary-base);
    border-color: var(--v-outline2-base);
}

.shifts-hint-bar {
    display: flex;
    align-items: center;
    gap: 8px;
}

.shifts-hint {
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}

.shifts-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
}

.shifts-empty-text {
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}

@media (max-width: 600px) {
    .shifts-hint-bar {
        flex-wrap: wrap;
    }

    .shifts-hint {
        flex: 1 0 100%;
        order: 2;
    }

    .shifts-actions {
        margin-left: 0;
        width: 100%;
    }
}
</style>
